import { CAPTURE } from '../../../config/constants.js';

/**
 * Spell-card capture (BULLET-HELL.md §D1).
 *
 * Survivor already owned 66 named, telegraphed, individually-designed boss
 * attacks and never told the player that dodging one cleanly was worth
 * anything. This is the scorekeeper that changes that: a card that runs
 * start-to-finish without landing a hit on the player is CAPTURED, and pays.
 * A card that lands a hit is failed silently — the damage was the punishment,
 * and stacking a penalty on top of it would make the fight's hardest moments
 * its least legible.
 *
 * Deliberately a listener, not a participant. It rides the card boundaries
 * SpawningSystem emits (`boss:pattern-start` / `-end` / `-abandon`) and
 * `player:damaged` / `bullet:grazed`, computes a payout, and emits
 * `boss:pattern-captured`. It never touches a pattern, a boss or the player,
 * which is why one instance covers every SpawningSystem in the run at once —
 * the primary boss, a second boss, and both heralds — without knowing that
 * more than one exists.
 *
 * Grazes RAISE a capture's payout rather than breaking it. That is the whole
 * point of the mechanic: the ceiling on a card is not "don't get hit", it's
 * "don't get hit while standing as close to the bullets as you can bear".
 */
export class CaptureTracker {
  constructor(eventBus) {
    this.eventBus = eventBus;
    // cardId -> { name, clean, grazes, desperation }
    this._open = new Map();
    // Per-pattern-name run tally, flushed into SaveManager at run end
    // (`recordPatternResults`) so the record survives the run that set it.
    // { [name]: { attempts, captures } }
    this.records = {};
    // Glimmer banked by captures this run. Paid at run end alongside the graze
    // payout rather than immediately, because Glimmer is a run-end currency
    // (Currency.calcGlimmer) and mid-run grants have nowhere to be shown.
    this.glimmer = 0;
    this.captures = 0;
    this.attempts = 0;

    eventBus.on('boss:pattern-start', ({ id, name, desperation }) => {
      if (id === undefined) return;
      this._open.set(id, { name, clean: true, grazes: 0, desperation: !!desperation });
    });
    eventBus.on('boss:pattern-end', (payload) => this._resolve(payload));
    eventBus.on('boss:pattern-abandon', ({ id }) => this._open.delete(id));
    // Only damage that actually stuck (Player emits this past i-frames, dodges
    // and Aegis plates), and it breaks EVERY card in flight: when two patterns
    // are layered the player is dodging one composite thing, and crediting a
    // capture to whichever of the two didn't fire the fatal bullet would be
    // scoring a dodge they didn't make.
    eventBus.on('player:damaged', () => {
      for (const card of this._open.values()) card.clean = false;
    });
    eventBus.on('bullet:grazed', () => {
      for (const card of this._open.values()) card.grazes++;
    });
    // A death ends every card unresolved — it isn't a capture, and it isn't an
    // attempt worth recording either, since the run stopped. Same for a boss
    // dying: SpawningSystem abandons its own in-flight cards, but the systems
    // themselves are discarded at that point (Game nulls the slot in several
    // paths), so this is the belt-and-braces that guarantees nothing stays open
    // across a fight boundary and gets failed by a hit taken in the next one.
    eventBus.on('player:died', () => this._open.clear());
    eventBus.on('boss:died', () => this._open.clear());
    eventBus.on('miniboss:died', () => this._open.clear());
  }

  _resolve({ id, name, danger = 0, desperation = false }) {
    const card = this._open.get(id);
    if (!card) return;
    this._open.delete(id);

    const record = this.records[name] ?? (this.records[name] = { attempts: 0, captures: 0 });
    record.attempts++;
    this.attempts++;
    if (!card.clean) return;
    record.captures++;
    this.captures++;

    const payout = capturePayout({ danger, grazes: card.grazes, desperation: desperation || card.desperation });
    this.glimmer += payout.glimmer;
    this.eventBus.emit('boss:pattern-captured', {
      name,
      grazes: card.grazes,
      desperation: desperation || card.desperation,
      ...payout,
    });
  }
}

/**
 * Prices one captured card. Exported for testing and for the tuning bench —
 * the payout curve is the part of §D1 most likely to need a second pass, and
 * it should be adjustable without reading the tracker.
 */
export function capturePayout({ danger = 0, grazes = 0, desperation = false } = {}) {
  const units = Math.min(Math.max(danger, 0) / CAPTURE.dangerPerUnit, CAPTURE.unitsMax);
  const grazeBonus = 1 + Math.min(grazes * CAPTURE.grazeBonusPerGraze, CAPTURE.grazeBonusMax);
  const multiplier = grazeBonus * (desperation ? CAPTURE.desperationMultiplier : 1);
  return {
    units,
    multiplier,
    glimmer: Math.round((CAPTURE.glimmerBase + CAPTURE.glimmerPerUnit * units) * multiplier),
    charge: (CAPTURE.chargeBase + CAPTURE.chargePerUnit * units) * multiplier,
    moteCount: Math.min(
      Math.round((CAPTURE.moteCountBase + CAPTURE.moteCountPerUnit * units)
        * (desperation ? CAPTURE.desperationMultiplier : 1)),
      CAPTURE.moteCountMax,
    ),
    // Total XP across the burst, not per mote — the caller splits it.
    moteXp: Math.round((CAPTURE.moteXpBase + CAPTURE.moteXpPerUnit * units) * multiplier),
  };
}
